import firebase from "firebase/app";
import useSWR from "swr";

import type { Event } from "@spwn/types/firebase/firestore";

export type DashboardEvent = {
  eventId: string;
  eventName: string;
  eventDateSeconds: number;
};

type DashboardEventDocument = {
  eventId: string;
  event: Pick<Event, "title" | "datetime"> | undefined;
};

/**
 * Firestoreの取得結果を、管理ダッシュボードで使うイベント情報へ変換する。
 * 存在しないドキュメントは`event: undefined`として除外するため、同時に取得できた
 * 他のイベントはそのまま利用できる。
 */
const createDashboardEvents = (
  documents: readonly DashboardEventDocument[]
): DashboardEvent[] =>
  documents.flatMap(({ eventId, event }) => {
    if (!event?.title) {
      return [];
    }

    return [
      {
        eventId,
        eventName: event.title.replace(/<(\/|)br ?(\/|)>/g, "\n"),
        eventDateSeconds: event.datetime.seconds,
      },
    ];
  });

const GetDashboardEventsKey = "getDashboardEvents";

export const useGetDashboardEvents = (eventIds: string[]) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const {
    data: events = [],
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<DashboardEvent[], Error>(
    currentUserId && uniqueEventIds.length > 0
      ? [GetDashboardEventsKey, currentUserId, uniqueEventIdsKey]
      : null,
    async () => {
      const snapshots = await Promise.all(
        uniqueEventIds.map(async (eventId) => {
          return firebase.firestore().collection("events").doc(eventId).get();
        })
      );

      return createDashboardEvents(
        snapshots.map((snapshot) => {
          if (!snapshot.exists) {
            // Firestoreは存在しないドキュメントも`exists: false`のsnapshotとして返す。
            return {
              eventId: snapshot.id,
              event: undefined,
            };
          }

          return {
            eventId: snapshot.id,
            event: snapshot.data() as Event | undefined,
          };
        })
      );
    }
  );

  return {
    events,
    eventNames: events.map((event) => event.eventName),
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
